import React, { useCallback, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import * as XLSX from "xlsx";
import { db } from "../firebase/firebaseConfig";

/**
 * Exports all returns with status === "straggler" to an Excel file.
 * - Includes docId
 * - Flattens nested objects into dot.notation columns
 *
 * Update RETURNS_COLLECTION and STATUS_FIELD if your old system differs.
 */
const RETURNS_COLLECTION = "returns_draft";     // <-- change to your OLD collection name
const STATUS_FIELD = "status";            // <-- change if different

function isPlainObject(v) {
  return Object.prototype.toString.call(v) === "[object Object]";
}

function flatten(obj, prefix = "", out = {}) {
  if (!isPlainObject(obj)) return out;

  Object.entries(obj).forEach(([k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;

    if (v === null || v === undefined) {
      out[key] = "";
      return;
    }

    // Firestore Timestamp often has toDate()
    if (v && typeof v.toDate === "function") {
      out[key] = v.toDate().toISOString();
      return;
    }

    if (Array.isArray(v)) {
      // Arrays: stringify (keeps data without exploding columns)
      out[key] = JSON.stringify(v);
      return;
    }

    if (isPlainObject(v)) {
      flatten(v, key, out);
      return;
    }

    out[key] = v;
  });

  return out;
}

export default function StragglerExport() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const runExport = useCallback(async () => {
    setBusy(true);
    setMsg("Querying stragglers…");

    try {
      const q = query(
        collection(db, RETURNS_COLLECTION),
        where(STATUS_FIELD, "==", "straggler")
      );

      const snap = await getDocs(q);

      setMsg(`Found ${snap.size} stragglers. Building rows…`);

      const rows = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        const flat = flatten(data);

        rows.push({
          docId: d.id,
          ...flat,
        });
      });

      if (!rows.length) {
        setMsg("No stragglers found. Nothing to export.");
        return;
      }

      setMsg("Generating Excel…");

      // Make stable column order: docId first, then sorted keys
      const allKeys = new Set();
      rows.forEach((r) => Object.keys(r).forEach((k) => allKeys.add(k)));
      const keys = Array.from(allKeys);
      keys.sort((a, b) => (a === "docId" ? -1 : b === "docId" ? 1 : a.localeCompare(b)));

      const aoa = [keys];
      rows.forEach((r) => aoa.push(keys.map((k) => (k in r ? r[k] : ""))));

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "stragglers");

      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      XLSX.writeFile(wb, `stragglers-${stamp}.xlsx`);

      setMsg(`Done. Exported ${rows.length} row(s).`);
    } catch (e) {
      console.error(e);
      setMsg(`Failed: ${e?.message || "unknown error"}`);
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white border border-gray-200 rounded-xl">
      <h2 className="text-xl font-semibold mb-2">Export Stragglers (Old System)</h2>
      <p className="text-sm text-gray-600 mb-4">
        Exports all docs where <code>{STATUS_FIELD}</code> is <code>straggler</code> from{" "}
        <code>{RETURNS_COLLECTION}</code>.
      </p>

      <button
        type="button"
        disabled={busy}
        onClick={runExport}
        className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
      >
        {busy ? "Working…" : "Export to Excel"}
      </button>

      {msg && <div className="mt-3 text-sm text-gray-700">{msg}</div>}
    </div>
  );
}
